import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';

// ========================================
// TYPES & CONSTANTS
// ========================================

const AUTH_TOKEN_KEY = 'admin_auth_token';
const USER_DATA_KEY = 'admin_user_data';

// ========================================
// CONTEXT
// ========================================

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);

  // Computed values
  const isAuthenticated = !!user && !!token;
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'manager';
  const isTelecaller = user?.role === 'telecaller';

  // Load stored auth on mount - don't verify, just restore
  useEffect(() => {
    const loadAuth = () => {
      try {
        const storedToken = localStorage.getItem(AUTH_TOKEN_KEY);
        const storedUser = localStorage.getItem(USER_DATA_KEY);

        if (storedToken && storedUser) {
          try {
            const parsedUser = JSON.parse(storedUser);

            // Check if user has admin role - only admin, super_admin, or manager can access
            const adminRoles = ['admin', 'super_admin', 'manager'];
            if (!adminRoles.includes(parsedUser?.role)) {
              // Non-admin user stored - clear storage
              console.warn('Non-admin user found in storage, clearing...');
              localStorage.removeItem(AUTH_TOKEN_KEY);
              localStorage.removeItem(USER_DATA_KEY);
              setToken(null);
              setUser(null);
            } else {
              setToken(storedToken);
              setUser(parsedUser);
            }
          } catch (parseError) {
            console.error('Error parsing stored user data:', parseError);
            localStorage.removeItem(AUTH_TOKEN_KEY);
            localStorage.removeItem(USER_DATA_KEY);
            setToken(null);
            setUser(null);
          }
        }
      } catch (error) {
        console.error('Error loading auth:', error);
        localStorage.removeItem(AUTH_TOKEN_KEY);
        localStorage.removeItem(USER_DATA_KEY);
      } finally {
        setIsLoading(false);
        setIsInitialized(true);
      }
    };

    loadAuth();
  }, []);

  // Login function
  const login = useCallback(async (identifier, password) => {
    try {
      setIsLoading(true);
      const response = await api.post('/auth/admin-login', {
        email: identifier,
        password,
      });

      if (response.data?.success && response.data?.data) {
        const { user: apiUser, token: authToken } = response.data.data;

        if (!authToken) {
          return {
            success: false,
            error: 'No token received from server. Please try again.',
          };
        }

        // Store auth data
        localStorage.setItem(AUTH_TOKEN_KEY, authToken);
        localStorage.setItem(USER_DATA_KEY, JSON.stringify(apiUser));

        setToken(authToken);
        setUser(apiUser);

        return {
          success: true,
          role: apiUser.role,
          user: apiUser
        };
      }

      return {
        success: false,
        error: response.data?.message || 'Login failed',
      };
    } catch (error) {
      console.error('Login error:', error);
      console.error('Error response:', error.response);

      // Handle 403 Forbidden (non-admin access)
      if (error.response?.status === 403) {
        const errorMessage = error.response?.data?.message || 'Access denied. This admin panel is for administrators only.';
        console.log('Returning 403 error:', errorMessage);
        return {
          success: false,
          error: errorMessage,
        };
      }

      // Handle other error statuses
      const errorMessage = error.response?.data?.message || error.message || 'Login failed. Please try again.';
      console.log('Returning error:', errorMessage);
      return {
        success: false,
        error: errorMessage,
      };
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Logout function
  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout').catch(() => {});
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem(AUTH_TOKEN_KEY);
      localStorage.removeItem(USER_DATA_KEY);
      setToken(null);
      setUser(null);
      // Use window.location for redirect since useNavigate can't be used outside Router
      window.location.href = '/login';
    }
  }, []);

  // Refresh user data
  const refreshUser = useCallback(async () => {
    if (!token) return false;

    try {
      const response = await api.get('/auth/me');
      if (response.data?.success && response.data?.data) {
        const apiUser = response.data.data;
        localStorage.setItem(USER_DATA_KEY, JSON.stringify(apiUser));
        setUser(apiUser);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Refresh user error:', error);
      return false;
    }
  }, [token]);

  // Update user locally
  const updateUser = useCallback((updates) => {
    setUser((prev) => {
      if (!prev) return null;
      const updated = { ...prev, ...updates };
      localStorage.setItem(USER_DATA_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  const value = {
    user,
    token,
    isLoading,
    isInitialized,
    isAuthenticated,
    isAdmin,
    isTelecaller,
    login,
    logout,
    refreshUser,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// ========================================
// HOOK
// ========================================

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;